import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { FileAudio, FileText, Film, Image as ImageIcon, Loader2, Paperclip, Search, Upload } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const KB = (n) => `${Math.max(1, Math.round((n || 0) / 1024))} KB`;

/** Everything uploaded to the KB — pick an asset or upload a new one. */
export function KbMediaLibrary({ open, onOpenChange, kind = "", onPick }) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState(kind);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/kb/media", { params: { q, kind: filter, limit: 60 } })
      .then((r) => setRows(r.data.assets || []))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [q, filter]);

  useEffect(() => { if (open) setFilter(kind); }, [open, kind]);
  useEffect(() => { if (open) { const t = setTimeout(load, 200); return () => clearTimeout(t); } }, [open, load]);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const isImage = file.type.startsWith("image/");
    const isMedia = /^(audio|video)\//.test(file.type) || file.type === "application/pdf"
      || /\.(mov|mp4|webm|mp3|wav|ogg|flac|m4a|pdf)$/i.test(file.name);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const route = isImage ? "/kb/upload" : isMedia ? "/kb/upload-media" : "/kb/upload-file";
      const { data } = await api.post(route, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      onPick?.({ ...data, kind: data.kind || "image", filename: file.name });
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally { setUploading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="kb-media-library">
        <DialogHeader><DialogTitle>Media library</DialogTitle></DialogHeader>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[200px] flex-1">
            <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
            <Input data-testid="kb-media-search" value={q} placeholder="Search file name…"
              onChange={(e) => setQ(e.target.value)} className="h-8 pl-8 text-xs" />
          </div>
          {[["", "All"], ["image", "Images"], ["video", "Video"], ["audio", "Audio"], ["pdf", "PDFs"], ["file", "Files"]].map(([v, lbl]) => (
            <button key={v || "all"} type="button" data-testid={`kb-media-filter-${v || "all"}`}
              onClick={() => setFilter(v)}
              className={`rounded-full border px-2.5 py-1 text-xs font-medium ${
                filter === v ? "border-primary bg-primary/10 text-primary" : "border-slate-200 dark:border-slate-800 text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800"}`}>
              {lbl}
            </button>
          ))}
          <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-slate-200 dark:border-slate-800 px-2.5 py-1 text-xs font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800">
            {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
            Upload
            <input type="file" className="hidden" data-testid="kb-media-upload"
              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; upload(f); }} />
          </label>
        </div>

        {loading ? (
          <div className="flex h-48 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-300 dark:text-slate-600" /></div>
        ) : rows.length === 0 ? (
          <div className="py-12 text-center text-sm text-slate-400 dark:text-slate-500" data-testid="kb-media-empty">Nothing uploaded yet.</div>
        ) : (
          <div className="grid max-h-[55vh] grid-cols-2 gap-2 overflow-y-auto sm:grid-cols-4">
            {rows.map((a) => (
              <button key={a.asset_id} type="button" data-testid={`kb-media-pick-${a.asset_id}`}
                onClick={() => { onPick?.(a); onOpenChange(false); }}
                className="group overflow-hidden rounded-lg border border-slate-200 dark:border-slate-800 text-left transition-colors hover:border-primary/50">
                <div className="flex h-24 items-center justify-center bg-slate-50 dark:bg-slate-900/60">
                  {a.kind === "image" ? (
                    <img src={a.url} alt={a.filename} className="h-24 w-full object-cover" />
                  ) : a.kind === "audio" ? <FileAudio className="h-8 w-8 text-slate-300 dark:text-slate-600" />
                    : a.kind === "video" ? <Film className="h-8 w-8 text-slate-300 dark:text-slate-600" />
                    : a.kind === "file" ? <Paperclip className="h-8 w-8 text-slate-300 dark:text-slate-600" />
                    : a.kind === "pdf" ? <FileText className="h-8 w-8 text-slate-300 dark:text-slate-600" />
                    : <ImageIcon className="h-8 w-8 text-slate-300 dark:text-slate-600" />}
                </div>
                <div className="px-2 py-1.5">
                  <div className="truncate text-[11px] font-medium text-slate-700 dark:text-slate-200">{a.filename || a.asset_id}</div>
                  <div className="text-[10px] text-slate-400 dark:text-slate-500">{a.kind} · {KB(a.size)}</div>
                </div>
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
